import React from 'react';
import { WarningIcon } from './Icons';
import { formatXml } from '../utils/xmlHelpers';

const UpdateConfirmationModal = ({ 
  isOpen, onClose, onConfirm, recordCount, objectName, xmlSnippet, mode,
  isRateTable = false, rateTableSummary = null,
  isDocEntry = false, documentSummary = null,
  isTransRule = false, transRuleSummary = null,
  isContractLineEntry = false, contractLineSummary = null
}) => {
  if (!isOpen) return null;
  
  let title, buttonText, warningText;
  
  if (isTransRule) {
    title = 'Confirm Transaction Rule Create';
    buttonText = 'Create Transaction Rules';
    warningText = null;
  } else if (isRateTable) {
    if (mode === 'create') {
      title = 'Confirm Rate Table Create';
      buttonText = 'Create Rate Tables';
    } else {
      title = 'Confirm Rate Table Update';
      buttonText = 'Replace Entries';
    }
    warningText = null;
  } else if (isContractLineEntry) {
    title = 'Confirm Contract Line Entry Update';
    buttonText = 'Update Entries';
    warningText = null;
  } else if (isDocEntry) {
    title = 'Confirm Document Update';
    buttonText = 'Update Lines';
    warningText = null;
  } else if (mode === 'create') {
    title = 'Confirm Create';
    buttonText = 'Create Records';
    warningText = "This action will create new records in Intacct.";
  } else {
    title = 'Confirm Update';
    buttonText = 'Update Records';
    warningText = "This action will modify data directly in Intacct. Ensure your Excel selection contains correct 'RECORDNO' values.";
  }
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl flex flex-col">
        <div className={`p-4 border-b rounded-t-lg flex items-center gap-2 ${
          isTransRule ? 'bg-purple-50' : isRateTable ? 'bg-orange-50' : (isDocEntry || isContractLineEntry) ? 'bg-blue-50' : 'bg-yellow-50'
        }`}>
          <WarningIcon />
          <h3 className="font-bold text-gray-800">{title}</h3>
        </div>
        
        <div className="p-6">
          {/* Transaction Rule Specific Content */}
          {isTransRule && transRuleSummary && (
            <>
              <p className="text-gray-700 mb-4">
                You are about to create <span className="font-bold text-purple-600">{transRuleSummary.totalRules}</span> transaction rule(s) 
                with <span className="font-bold text-blue-600">{transRuleSummary.totalDetails}</span> detail line(s).
              </p>
              
              <div className="bg-purple-50 border-l-4 border-purple-500 p-4 mb-4">
                <p className="text-sm text-purple-800 font-bold">ℹ️ TRANSACTION RULE CREATE</p>
                <p className="text-xs text-purple-700 mt-1">
                  Each unique NAME in your data creates one transaction rule. 
                  All rows with the same NAME become detail lines within that rule.
                </p>
              </div>
              
              <div className="bg-gray-50 border rounded p-3 mb-4">
                <p className="text-xs font-bold text-gray-600 uppercase mb-2">Transaction Rules to Create:</p>
                <div className="max-h-32 overflow-y-auto">
                  {transRuleSummary.transactionRules.map((rule, idx) => (
                    <div key={idx} className="flex justify-between items-center py-1 px-2 text-sm border-b last:border-b-0">
                      <span className="font-mono text-gray-800">{rule.name}</span>
                      <span className="text-xs text-gray-500">{rule.detailCount} {rule.detailCount === 1 ? 'detail' : 'details'}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
          
          {/* Rate Table Specific Content */}
          {isRateTable && rateTableSummary && (
            <>
              <p className="text-gray-700 mb-4">
                You are about to {mode === 'create' ? 'create' : 'update'} <span className="font-bold text-orange-600">{rateTableSummary.totalRateTables}</span> rate table(s) 
                with <span className="font-bold text-blue-600">{rateTableSummary.totalEntries}</span> {rateTableSummary.entryTypeName} entries.
              </p>
              
              {/* Only show destructive warning for updates, not creates */}
              {mode !== 'create' && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
                  <p className="text-sm text-red-800 font-bold">⚠️ DESTRUCTIVE OPERATION</p>
                  <p className="text-xs text-red-700 mt-1">
                    This will <strong>REPLACE ALL</strong> {rateTableSummary.containerName} entries for the rate table(s) below. 
                    Any existing entries of this type that are NOT in your selection will be <strong>DELETED</strong>.
                  </p>
                </div>
              )}
              
              {/* Show additive info for creates */}
              {mode === 'create' && (
                <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4">
                  <p className="text-sm text-green-800 font-bold">➕ ADDITIVE OPERATION</p>
                  <p className="text-xs text-green-700 mt-1">
                    This will <strong>ADD</strong> new entries to the rate table(s) below. 
                    Existing entries will NOT be affected.
                  </p>
                </div>
              )}
              
              <div className="bg-gray-50 border rounded p-3 mb-4">
                <p className="text-xs font-bold text-gray-600 uppercase mb-2">Rate Tables to {mode === 'create' ? 'Create' : 'Update'}:</p>
                <div className="max-h-32 overflow-y-auto">
                  {rateTableSummary.rateTables.map((rt, idx) => (
                    <div key={idx} className="flex justify-between items-center py-1 px-2 text-sm border-b last:border-b-0">
                      <span className="font-mono text-gray-800">{rt.id}</span>
                      <span className="text-xs text-gray-500">{rt.entryCount} {rt.entryCount === 1 ? 'entry' : 'entries'}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
          
          {/* Contract Line Entry Specific Content */}
          {isContractLineEntry && contractLineSummary && (
            <>
              <p className="text-gray-700 mb-4">
                You are about to update <span className="font-bold text-blue-600">{contractLineSummary.totalParentLines}</span> contract line(s) 
                with <span className="font-bold text-blue-600">{contractLineSummary.totalEntries}</span> line entries.
              </p>
              
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
                <p className="text-sm text-blue-800 font-bold">ℹ️ CONTRACT LINE ENTRY UPDATE</p>
                <p className="text-xs text-blue-700 mt-1">
                  Entries with a <strong>RECORDNO</strong> will be updated. Entries without a RECORDNO will be <strong>created as new</strong>.
                  Rows are grouped by <strong>{contractLineSummary.parentKeyHeader}</strong> into one API call per contract line.
                </p>
              </div>
              
              <div className="bg-gray-50 border rounded p-3 mb-4">
                <p className="text-xs font-bold text-gray-600 uppercase mb-2">Contract Lines to Update:</p>
                <div className="max-h-32 overflow-y-auto">
                  {contractLineSummary.parentLines.map((pl, idx) => (
                    <div key={idx} className="flex justify-between items-center py-1 px-2 text-sm border-b last:border-b-0">
                      <span className="font-mono text-gray-800">{pl.id}</span>
                      <span className="text-xs text-gray-500">
                        {pl.updateCount > 0 && <span className="text-blue-600">{pl.updateCount} update{pl.updateCount !== 1 ? 's' : ''}</span>}
                        {pl.updateCount > 0 && pl.createCount > 0 && ' · '}
                        {pl.createCount > 0 && <span className="text-green-600">{pl.createCount} new</span>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {contractLineSummary.newEntryCount > 0 && (
                <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4">
                  <p className="text-xs text-green-800 font-bold">➕ NEW ENTRIES</p>
                  <p className="text-xs text-green-700 mt-1">
                    <strong>{contractLineSummary.newEntryCount}</strong> row(s) have no RECORDNO and will be created as new entries.
                  </p>
                </div>
              )}
            </>
          )}
          
          {/* Document Entry Specific Content */}
          {isDocEntry && documentSummary && (
            <>
              <p className="text-gray-700 mb-4">
                You are about to update <span className="font-bold text-blue-600">{documentSummary.totalDocuments}</span> document(s) 
                with <span className="font-bold text-blue-600">{documentSummary.totalLines}</span> line item(s).
              </p>
              
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
                <p className="text-sm text-blue-800 font-bold">ℹ️ NON-DESTRUCTIVE UPDATE</p>
                <p className="text-xs text-blue-700 mt-1">
                  Only the fields you've included will be updated. Other fields on each line will remain unchanged.
                  Line numbers from your query (0-based) will be converted to 1-based for the update.
                </p>
              </div>
              
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                <p className="text-xs text-yellow-800 font-bold">⚠️ STATE RESTRICTION</p>
                <p className="text-xs text-yellow-700 mt-1">
                  Documents with state <strong>Submitted</strong>, <strong>In Progress</strong>, <strong>Converted</strong>, or <strong>Partially Converted</strong> cannot be updated.
                </p>
              </div>
              
              <div className="bg-gray-50 border rounded p-3 mb-4">
                <p className="text-xs font-bold text-gray-600 uppercase mb-2">Documents to Update:</p>
                <div className="max-h-32 overflow-y-auto">
                  {documentSummary.documents.map((doc, idx) => (
                    <div key={idx} className="flex justify-between items-center py-1 px-2 text-sm border-b last:border-b-0">
                      <span className="font-mono text-gray-800">{doc.id}</span>
                      <span className="text-xs text-gray-500">{doc.lineCount} {doc.lineCount === 1 ? 'line' : 'lines'}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
          
          {/* Standard Update/Create Content */}
          {!isRateTable && !isDocEntry && !isTransRule && !isContractLineEntry && (
            <>
              <p className="text-gray-700 mb-4">
                You are about to {mode} <span className="font-bold text-blue-600">{recordCount}</span> records in <span className="font-bold">{objectName}</span>.
              </p>
              
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                <p className="text-xs text-yellow-800 font-bold">WARNING:</p>
                <p className="text-xs text-yellow-700">{warningText}</p>
              </div>
            </>
          )}
          
          {recordCount > 1000 && !isRateTable && !isDocEntry && !isTransRule && !isContractLineEntry && (
            <div className="bg-orange-50 border-l-4 border-orange-500 p-4 mb-4">
              <p className="text-xs text-orange-800 font-bold">PERFORMANCE WARNING:</p>
              <p className="text-xs text-orange-700">You are processing {recordCount.toLocaleString()} records. This may take several minutes or timeout.</p>
            </div>
          )}
          
          <div className="mt-2">
            <p className="text-xs font-bold text-gray-500 uppercase mb-1">
              {isTransRule ? 'XML Preview (First Transaction Rule)' : isRateTable ? 'XML Preview (First Rate Table)' : isContractLineEntry ? 'XML Preview (First Contract Line)' : isDocEntry ? 'XML Preview (First Document)' : 'XML Preview (First Record)'}
            </p>
            <div className="bg-gray-900 p-3 rounded overflow-auto max-h-40">
              <pre className="text-xs text-green-400 font-mono whitespace-pre-wrap">{formatXml(xmlSnippet)}</pre>
            </div>
          </div>
        </div>
        
        <div className="p-4 border-t bg-gray-50 flex justify-end gap-2 rounded-b-lg">
          <button onClick={onClose} className="px-4 py-2 bg-white border border-gray-300 rounded text-gray-700 font-medium hover:bg-gray-50">Cancel</button>
          <button onClick={onConfirm} className={`px-4 py-2 text-white rounded font-medium shadow-sm ${
            isTransRule
              ? 'bg-purple-600 hover:bg-purple-700'
              : isRateTable 
                ? (mode === 'create' ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-600 hover:bg-orange-700')
                : (isDocEntry || isContractLineEntry)
                  ? 'bg-blue-600 hover:bg-blue-700' 
                  : 'bg-red-600 hover:bg-red-700'
          }`}>{buttonText}</button>
        </div>
      </div>
    </div>
  );
};

export default UpdateConfirmationModal;
